/**
 * Collection scheduler — manages periodic fetching from data sources.
 * Configurable intervals per source. Exponential backoff on failure.
 * Per-source circuit breaker suspends sources after repeated failures.
 */

import type { Source } from '@/sources/base-source';
import { processBatch } from './pipeline';
import { eventBus } from './events';
import { CircuitBreaker } from '@/utils/circuit-breaker';

interface ScheduledTask {
  source: Source;
  intervalMs: number;
  timerId: ReturnType<typeof setTimeout> | null;
  failureCount: number;
  running: boolean;
  /** Timestamp of last successful collection start. */
  lastCollectedAt: number;
}

const MAX_BACKOFF_MS = 5 * 60 * 1000; // 5 minutes
const BASE_BACKOFF_MS = 5_000;
const CIRCUIT_FAILURE_THRESHOLD = 3;
const CIRCUIT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
/** Default fetch timeout per source (ms). Prevents hung upstream APIs from blocking a slot. */
const FETCH_TIMEOUT_MS = 30_000;

/** Hidden tab multiplier — polls 4x slower when tab is not visible. */
const HIDDEN_TAB_MULTIPLIER = 4;
/** Jitter range — ±10% random offset on intervals to avoid thundering herd. */
const JITTER_FACTOR = 0.1;
/** Stale threshold — sources older than 1.5x their interval are refreshed on tab focus. */
const STALE_THRESHOLD_RATIO = 1.5;

/**
 * Race a promise against a timeout. Rejects with a descriptive error if the
 * timeout fires first. Does NOT abort the underlying operation — but frees
 * the scheduler slot so other sources can proceed.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Fetch timeout after ${ms}ms for ${label}`)), ms);
    promise.then(
      (val) => { clearTimeout(timer); resolve(val); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

/** Apply ±10% random jitter to a delay value. */
function applyJitter(delayMs: number): number {
  const jitter = delayMs * JITTER_FACTOR * (2 * Math.random() - 1);
  return Math.max(1000, Math.round(delayMs + jitter));
}

export class CollectionScheduler {
  private tasks = new Map<string, ScheduledTask>();
  private breakers = new Map<string, CircuitBreaker>();
  private active = false;
  private tabHidden = false;
  private visibilityHandler: (() => void) | null = null;

  /** Register a source for periodic collection. */
  register(source: Source, intervalMs?: number): void {
    if (this.tasks.has(source.id)) return;
    this.tasks.set(source.id, {
      source,
      intervalMs: intervalMs ?? source.meta.updateInterval,
      timerId: null,
      failureCount: 0,
      running: false,
      lastCollectedAt: 0,
    });
    this.breakers.set(source.id, new CircuitBreaker({
      failureThreshold: CIRCUIT_FAILURE_THRESHOLD,
      cooldownMs: CIRCUIT_COOLDOWN_MS,
    }));
  }

  /** Unregister a source and stop its collection. */
  unregister(sourceId: string): void {
    const task = this.tasks.get(sourceId);
    if (task?.timerId) {
      clearTimeout(task.timerId);
    }
    this.tasks.delete(sourceId);
    this.breakers.delete(sourceId);
  }

  /** Priority tiers for initial fetch ordering. Lower number = higher priority. */
  private static readonly SOURCE_PRIORITY: Record<string, number> = {
    // Real-time sources fire first
    'oref': 0, 'opensky': 0, 'ais-ws': 0,
    // Core intel sources next
    'usgs': 1, 'firms': 1, 'gdacs': 1, 'gdelt': 1,
    // Slower-updating sources last
    'acled': 2, 'sanctions': 2, 'polymarket': 2, 'vesselfinder': 2,
  };

  /**
   * Start all registered tasks.
   * @param options.deferInitialFetch When true, only start periodic timers —
   *   the first real fetch happens at the source's interval (or when flushAll() is called).
   *   This lets the map render with static/cached data before network contention begins.
   */
  startAll(options?: { deferInitialFetch?: boolean }): void {
    this.active = true;

    // Set up visibility change listener for hidden tab multiplier + stale flush
    this.visibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.tabHidden = document.hidden;

    // Sort tasks by priority tier (real-time first, hourly last)
    const sorted = [...this.tasks.keys()].sort((a, b) => {
      const pa = CollectionScheduler.SOURCE_PRIORITY[a] ?? 1;
      const pb = CollectionScheduler.SOURCE_PRIORITY[b] ?? 1;
      return pa - pb;
    });

    if (options?.deferInitialFetch) {
      // Deferred mode: only start periodic refresh timers, no immediate fetch
      for (const id of sorted) {
        this.scheduleNext(id);
      }
    } else {
      // Immediate mode: stagger initial fetches by 200ms each
      let stagger = 0;
      for (const id of sorted) {
        setTimeout(() => {
          if (this.active) void this.collect(id);
        }, stagger);
        stagger += 200;
      }
    }
  }

  /**
   * Trigger staggered fetch for all sources that haven't collected yet.
   * Call after the map has rendered to start live data collection.
   */
  flushAll(): void {
    if (!this.active) return;

    const sorted = [...this.tasks.keys()].sort((a, b) => {
      const pa = CollectionScheduler.SOURCE_PRIORITY[a] ?? 1;
      const pb = CollectionScheduler.SOURCE_PRIORITY[b] ?? 1;
      return pa - pb;
    });

    let stagger = 0;
    for (const id of sorted) {
      const task = this.tasks.get(id);
      if (!task || task.lastCollectedAt > 0) continue; // already fetched
      // Cancel the deferred periodic timer so we don't double-fetch
      if (task.timerId) {
        clearTimeout(task.timerId);
        task.timerId = null;
      }
      setTimeout(() => {
        if (this.active) void this.collect(id);
      }, stagger);
      stagger += 200;
    }
  }

  /** Stop all tasks. */
  stopAll(): void {
    this.active = false;
    for (const [, task] of this.tasks) {
      if (task.timerId) {
        clearTimeout(task.timerId);
        task.timerId = null;
      }
    }

    // Clean up visibility listener
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Handle visibility change:
   * - Tab hidden → reschedule all active tasks with 4x multiplied intervals
   * - Tab visible → flush stale sources (older than 1.5x their interval)
   */
  private handleVisibilityChange(): void {
    const wasHidden = this.tabHidden;
    this.tabHidden = document.hidden;

    if (this.tabHidden) {
      // Going hidden — reschedule all pending tasks with longer intervals
      for (const [sourceId, task] of this.tasks) {
        if (task.timerId) {
          clearTimeout(task.timerId);
          task.timerId = null;
          this.scheduleNext(sourceId);
        }
      }
    } else if (wasHidden) {
      // Coming back — flush stale sources immediately
      const now = Date.now();
      for (const [sourceId, task] of this.tasks) {
        if (task.running) continue;

        const staleThreshold = task.intervalMs * STALE_THRESHOLD_RATIO;
        const age = now - task.lastCollectedAt;

        if (task.lastCollectedAt > 0 && age > staleThreshold) {
          // Cancel any pending timer and collect immediately
          if (task.timerId) {
            clearTimeout(task.timerId);
            task.timerId = null;
          }
          void this.collect(sourceId);
        } else if (task.timerId) {
          // Reschedule with normal (non-hidden) intervals
          clearTimeout(task.timerId);
          task.timerId = null;
          this.scheduleNext(sourceId);
        }
      }
    }
  }

  private scheduleNext(sourceId: string): void {
    const task = this.tasks.get(sourceId);
    if (!task || !this.active) return;

    let delay = task.failureCount > 0
      ? Math.min(BASE_BACKOFF_MS * Math.pow(2, task.failureCount - 1), MAX_BACKOFF_MS)
      : task.intervalMs;

    // Apply 4x multiplier when tab is hidden
    if (this.tabHidden) {
      delay *= HIDDEN_TAB_MULTIPLIER;
    }

    // Apply ±10% jitter to prevent thundering herd
    delay = applyJitter(delay);

    task.timerId = setTimeout(() => {
      void this.collect(sourceId);
    }, delay);
  }

  private async collect(sourceId: string): Promise<void> {
    const task = this.tasks.get(sourceId);
    if (!task || task.running || !this.active) return;

    // If circuit breaker is open/suspended and cooldown hasn't expired, skip
    const breaker = this.breakers.get(sourceId);
    if (breaker && !breaker.canExecute()) {
      // Suspended sources don't get rescheduled
      if (breaker.getState() !== 'suspended') {
        this.scheduleNext(sourceId);
      }
      return;
    }

    task.running = true;
    task.lastCollectedAt = Date.now();
    try {
      const fetchFn = () => task.source.fetch();
      const events = breaker
        ? await withTimeout(breaker.execute(fetchFn), FETCH_TIMEOUT_MS, sourceId)
        : await withTimeout(fetchFn(), FETCH_TIMEOUT_MS, sourceId);
      task.failureCount = 0;
      eventBus.emit('source:status', { sourceId, state: 'active' });

      if (events.length > 0) {
        await processBatch(events);
      }
    } catch (err) {
      task.failureCount++;

      // Log circuit-open once on transition, suppress repeated failure noise
      const breakerState = breaker?.getState();
      if (breakerState === 'suspended') {
        console.debug(
          `[Sentinel] Source ${sourceId} suspended for session after ${breaker!.getTripCount()} circuit trips`,
        );
        eventBus.emit('source:status', { sourceId, state: 'suspended' });
      } else if (breakerState === 'open') {
        const cooldownSec = Math.round(breaker!.getCurrentCooldown() / 1000);
        console.debug(
          `[Sentinel] Circuit open for ${sourceId} — cooldown ${cooldownSec}s (trip #${breaker!.getTripCount()})`,
        );
        eventBus.emit('source:status', { sourceId, state: 'circuit-open' });
      } else {
        eventBus.emit('source:status', { sourceId, state: 'error' });
        // Only log first failure per source; suppress repeated failures
        // to avoid spamming console for flaky external APIs
        if (task.failureCount <= 1) {
          console.debug(`[Sentinel] Collection failed for ${sourceId}:`, err);
        }
      }
    } finally {
      task.running = false;
      this.scheduleNext(sourceId);
    }
  }
}

/** Singleton scheduler */
export const scheduler = new CollectionScheduler();
